/**
 * Public identifier formats (docs/VOCABULARY.md → Identifiers, §12.2, §11.1).
 *
 * Win ID: 10 characters of Crockford base32 — 9 payload characters (45 bits) followed by one check character
 * (Crockford mod-37 check symbol). Displayed as `WIN-XXXXX-XXXXX`; accepted in any case, with or without the
 * prefix and hyphens, and with the Crockford confusables (I, L → 1, O → 0) normalized (D-101).
 */
import { z } from 'zod';
export const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** Check symbols for Crockford's mod-37 check: the 32 alphabet symbols plus `*~$=U`. */
export const CROCKFORD_CHECK_ALPHABET = `${CROCKFORD_ALPHABET}*~$=U`;
export const WIN_ID_PAYLOAD_LENGTH = 9;
export const WIN_ID_LENGTH = 10;
export const WIN_ID_PREFIX = 'WIN';
const crockfordValue = (ch) => CROCKFORD_ALPHABET.indexOf(ch);
/** Compute the Crockford mod-37 check character for a canonical payload. */
export const winIdCheckChar = (payload) => {
    let value = 0n;
    for (const ch of payload) {
        const v = crockfordValue(ch);
        if (v < 0)
            throw new RangeError(`invalid Crockford character: ${ch}`);
        value = value * 32n + BigInt(v);
    }
    const check = Number(value % 37n);
    return CROCKFORD_CHECK_ALPHABET.charAt(check);
};
/**
 * Normalize user input (`win-abcd...`, `WIN ABCDE-FGHJK`, lowercase, I/L/O confusables) to the canonical
 * 10-character form. Returns `null` when the input cannot be a win ID at all.
 */
export const normalizeWinId = (input) => {
    let s = input
        .trim()
        .toUpperCase()
        .replace(/[\s_-]/g, '');
    if (s.startsWith(WIN_ID_PREFIX))
        s = s.slice(WIN_ID_PREFIX.length);
    s = s.replace(/[IL]/g, '1').replace(/O/g, '0');
    if (s.length !== WIN_ID_LENGTH)
        return null;
    const payload = s.slice(0, WIN_ID_PAYLOAD_LENGTH);
    const check = s.charAt(WIN_ID_PAYLOAD_LENGTH);
    for (const ch of payload)
        if (crockfordValue(ch) < 0)
            return null;
    if (!CROCKFORD_CHECK_ALPHABET.includes(check))
        return null;
    return payload + check;
};
/** True when the input normalizes to a win ID whose check character verifies. */
export const isValidWinId = (input) => {
    const canonical = normalizeWinId(input);
    if (!canonical)
        return false;
    return (winIdCheckChar(canonical.slice(0, WIN_ID_PAYLOAD_LENGTH)) === canonical.charAt(WIN_ID_PAYLOAD_LENGTH));
};
/** Build a canonical win ID from 9 payload characters (the outcome module supplies random payload bytes). */
export const buildWinId = (payload) => {
    if (payload.length !== WIN_ID_PAYLOAD_LENGTH)
        throw new RangeError('win ID payload must be 9 characters');
    return payload + winIdCheckChar(payload);
};
/** Encode 45 bits from raw bytes (at least 6 bytes) into a 9-character payload and append the check character. */
export const winIdFromBytes = (bytes) => {
    if (bytes.length < 6)
        throw new RangeError('need at least 6 random bytes');
    let acc = 0n;
    for (let i = 0; i < 6; i++)
        acc = (acc << 8n) | BigInt(bytes[i] ?? 0);
    acc &= (1n << 45n) - 1n;
    let payload = '';
    for (let i = 0; i < WIN_ID_PAYLOAD_LENGTH; i++) {
        payload = CROCKFORD_ALPHABET.charAt(Number(acc & 31n)) + payload;
        acc >>= 5n;
    }
    return buildWinId(payload);
};
/** Display form `WIN-XXXXX-XXXXX`. Throws on an invalid id. */
export const formatWinId = (input) => {
    const canonical = normalizeWinId(input);
    if (!canonical || !isValidWinId(canonical))
        throw new RangeError('invalid win ID');
    return `${WIN_ID_PREFIX}-${canonical.slice(0, 5)}-${canonical.slice(5)}`;
};
/** Canonical win ID in transport: accepts any input form, outputs the 10-char canonical string. */
export const WinId = z
    .string()
    .transform((s, ctx) => {
    const canonical = normalizeWinId(s);
    if (!canonical || !isValidWinId(canonical)) {
        ctx.addIssue({ code: 'custom', message: 'invalid win ID' });
        return z.NEVER;
    }
    return canonical;
})
    .pipe(z.string().length(WIN_ID_LENGTH));
/** Claim token: 32 random bytes, base64url without padding (43 characters). Exchanged once for a claim session. */
export const CLAIM_TOKEN_BYTES = 32;
export const ClaimToken = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'invalid claim token');
/** Referral / share code in `/r/:code` links. */
export const ReferralCode = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{8,12}$/);
/**
 * Signed, limited-purpose win link (§12.2): `/w/:winId?exp=<unix seconds>&sig=<base64url HMAC>`.
 * The signature covers `winId + "." + exp` with a tenant-scoped key; admins open it from the winners board.
 */
export const WinLinkQuery = z.object({
    exp: z.coerce.number().int().positive(),
    sig: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});
export const WinLinkParams = z.object({ winId: WinId });
/** Support case reference shown to participants: `CASE-` + 8 Crockford characters. */
export const SupportCaseRef = z.string().regex(/^CASE-[0-9A-HJKMNP-TV-Z]{8}$/);
//# sourceMappingURL=ids.js.map